import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import isDoNotDisturbPath from 'src/constants/doNotDisturb';
import { useAppSelector } from 'src/redux';
import socket from 'src/socket';

/**
 * Tells the socket server whether this tab can be interrupted: "hidden" when it
 * is in the background, "busy" on a do-not-disturb screen. The server then skips
 * people with no free tab instead of sending invites the page would only hold
 * back unseen. Sent only when the state changes.
 */
const useReportAwayState = (enabled: boolean) => {
  const { pathname } = useLocation();
  const isSocketConnected = useAppSelector(
    (state) => state.appState.isSocketConnected,
  );
  const lastSent = useRef<string | null>(null);

  // A reconnect is a new socket the server knows nothing about: report again.
  useEffect(() => {
    lastSent.current = null;
  }, [isSocketConnected]);

  useEffect(() => {
    if (!enabled || !isSocketConnected) return undefined;
    const report = () => {
      let reason: 'hidden' | 'busy' | null = null;
      if (document.hidden) reason = 'hidden';
      else if (isDoNotDisturbPath(pathname)) reason = 'busy';
      const state = reason ?? 'free';
      if (state === lastSent.current) return;
      lastSent.current = state;
      socket.emit(
        'set_away',
        reason ? { away: true, reason } : { away: false },
      );
    };
    report();
    document.addEventListener('visibilitychange', report);
    return () => document.removeEventListener('visibilitychange', report);
  }, [enabled, isSocketConnected, pathname]);
};

export default useReportAwayState;
